import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { ProblemSolution } from '../components/ProblemSolution';
import { PageCTA } from '../components/PageCTA';
import { Layout, Smartphone, Share2, ArrowRight, BarChart3, Users, Zap, CheckCircle2 } from 'lucide-react';
// Animated Counter Component
function AnimatedCounter({
  value,
  prefix = '',
  suffix = ''
}: {
  value: number;
  prefix?: string;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 2000; // 2 seconds
      const increment = value / (duration / 16); // 60fps
      const timer = setInterval(() => {
        start += increment;
        if (start >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  return <span ref={ref} data-id="element-574">
      {prefix}
      {count}
      {suffix}
    </span>;
}
export function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    icon: <Layout size={28} data-id="element-575" />,
    title: 'Página Web Profesional',
    description: 'Transformamos visitantes en clientes recurrentes con una web rápida, persuasiva y diseñada a medida para maximizar tus ventas.',
    metric: 'Conversión',
    metricValue: '+120%',
    chartType: 'bar'
  }, {
    icon: <Smartphone size={28} data-id="element-576" />,
    title: 'Menú Digital Avanzado',
    description: 'Aumenta el ticket medio con una carta digital interactiva, visualmente irresistible y optimizada para una experiencia de usuario perfecta.',
    metric: 'Interacción',
    metricValue: '3x',
    chartType: 'donut'
  }, {
    icon: <Share2 size={28} data-id="element-577" />,
    title: 'Redes Sociales Sincronizadas',
    description: 'Multiplica tu alcance y autoridad con perfiles sociales estratégicos que conectan con tu audiencia y dirigen tráfico directo a tu negocio.',
    metric: 'Alcance',
    metricValue: '+85%',
    chartType: 'line'
  }];
  const metrics = [{
    icon: <BarChart3 size={32} data-id="element-578" />,
    value: <AnimatedCounter value={70} prefix="+" suffix="%" data-id="element-579" />,
    label: 'Más visibilidad online'
  }, {
    icon: <Users size={32} data-id="element-580" />,
    value: <AnimatedCounter value={3} prefix="+" suffix="x" data-id="element-581" />,
    label: 'Más interacción con clientes'
  }, {
    icon: <Zap size={32} data-id="element-582" />,
    value: <AnimatedCounter value={100} suffix="%" data-id="element-583" />,
    label: 'Tasa de conversión optimizada'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div className="pt-24" data-id="element-584">
      <Hero data-id="element-585" />
      <ProblemSolution data-id="element-586" />

      {/* Services Overview */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" data-id="element-587">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" data-id="element-588"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-[400px] bg-brand-emerald/5 blur-[120px] rounded-full pointer-events-none" data-id="element-589"></div>

        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-590">
          <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-591">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-592">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tighter" data-id="element-593">
                Un ecosistema digital{' '}
                <span className="text-gradient" data-id="element-594">completo</span>
              </h2>
              <p className="text-gray-400 text-xl font-medium" data-id="element-595">
                No hacemos piezas sueltas. Construimos una maquinaria donde cada
                elemento trabaja en conjunto para generar resultados.
              </p>
            </motion.div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16" data-id="element-596">
            {services.map((service, index) => <motion.div key={index} variants={itemVariants} className="card-premium p-8 group flex flex-col h-full relative overflow-hidden" data-id="element-597">
                {/* Subtle bottom highlight line on hover */}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-emerald to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-598"></div>

                <div className="absolute top-0 right-0 p-6 text-6xl font-black text-white/10 select-none pointer-events-none group-hover:text-brand-emerald/15 transition-colors duration-500" data-id="element-599">
                  0{index + 1}
                </div>
                <div className="icon-premium mb-6 group-hover:scale-110 transition-transform duration-500 relative z-10" data-id="element-600">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 relative z-10" data-id="element-601">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed relative z-10 font-medium mb-8 flex-grow" data-id="element-602">
                  {service.description}
                </p>

                {/* Visual Metric */}
                <div className="mt-auto pt-6 border-t border-white/10 relative z-10" data-id="element-603">
                  <div className="flex items-center justify-between mb-4" data-id="element-604">
                    <span className="text-sm font-bold text-gray-400 uppercase tracking-wider" data-id="element-605">
                      {service.metric}
                    </span>
                    <span className="text-xl font-black text-brand-emerald" data-id="element-606">
                      {service.metricValue}
                    </span>
                  </div>

                  <div className="h-12 w-full flex items-end justify-center" data-id="element-607">
                    {service.chartType === 'bar' && <div className="flex items-end gap-2 w-full h-full" data-id="element-608">
                        {[30, 50, 40, 70, 60, 100].map((h, i) => <motion.div key={i} initial={{
                    height: 0
                  }} whileInView={{
                    height: `${h}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.8,
                    delay: i * 0.1
                  }} className="flex-1 bg-gradient-to-t from-brand-emerald to-brand-emeraldLight rounded-sm opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-609" />)}
                      </div>}
                    {service.chartType === 'donut' && <div className="relative w-12 h-12 mx-auto" data-id="element-610">
                        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90" data-id="element-611">
                          <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" data-id="element-612" />
                          <motion.circle initial={{
                      strokeDasharray: '0 100'
                    }} whileInView={{
                      strokeDasharray: '75 100'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1.5,
                      ease: 'easeOut'
                    }} cx="18" cy="18" r="16" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" data-id="element-613" />
                        </svg>
                      </div>}
                    {service.chartType === 'line' && <svg viewBox="0 0 100 40" className="w-full h-full overflow-visible" data-id="element-614">
                        <motion.path initial={{
                    pathLength: 0
                  }} whileInView={{
                    pathLength: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1.5,
                    ease: 'easeInOut'
                  }} d="M0,30 Q20,35 40,20 T80,15 T100,5" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" data-id="element-615" />
                        <motion.circle initial={{
                    scale: 0
                  }} whileInView={{
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    delay: 1.5
                  }} cx="100" cy="5" r="3" fill="#fff" data-id="element-616" />
                      </svg>}
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          {/* Simple CTA Banner replacing the redundant pricing section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} animate={{
          boxShadow: ['0 0 0px rgba(16,185,129,0)', '0 0 30px rgba(16,185,129,0.1)', '0 0 0px rgba(16,185,129,0)'],
          borderColor: ['rgba(16,185,129,0.3)', 'rgba(16,185,129,0.5)', 'rgba(16,185,129,0.3)']
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="max-w-4xl mx-auto mb-16 bg-gradient-to-r from-[#111] to-[#0a1a12] border rounded-2xl p-8 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-617">
            <div className="absolute inset-0 bg-grid-pattern-emerald opacity-10" data-id="element-618"></div>
            <div className="relative z-10 flex-1 text-center md:text-left" data-id="element-619">
              <h3 className="text-2xl md:text-3xl font-black text-white mb-2" data-id="element-620">
                Todo esto por 350€ <span className="text-brand-emerald" data-id="element-621">—</span>{' '}
                Sin sorpresas
              </h3>
              <p className="text-gray-400 font-medium" data-id="element-622">
                Un único pago dividido en dos fases. Comenzamos a trabajar el
                mismo día del abono inicial.
              </p>
            </div>
            <div className="relative z-10 shrink-0" data-id="element-623">
              <Link to="/contacto" className="inline-flex justify-center items-center gap-2 bg-brand-emerald text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-brand-emeraldLight transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:-translate-y-1 group" data-id="element-624">
                Empezar ahora
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-625" />
              </Link>
            </div>
          </motion.div>

          <div className="text-center" data-id="element-626">
            <Link to="/servicios" className="inline-flex items-center gap-2 text-brand-emerald hover:text-brand-emeraldLight font-bold text-lg transition-colors group" data-id="element-627">
              Descubre nuestro sistema en detalle
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-628" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Results Preview */}
      <section className="py-24 bg-[#050505] relative overflow-hidden" data-id="element-629">
        <div className="section-divider absolute top-0" data-id="element-630"></div>
        <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay pointer-events-none" data-id="element-631"></div>

        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-632">
          <div className="max-w-6xl mx-auto bg-gradient-to-br from-[#111] to-[#0a1a12] rounded-3xl border border-brand-emerald/20 p-10 md:p-16 relative overflow-hidden shadow-[0_0_50px_rgba(16,185,129,0.05)]" data-id="element-633">
            <div className="absolute top-0 right-0 w-96 h-96 bg-brand-emerald/10 blur-[100px] rounded-full pointer-events-none" data-id="element-634"></div>
            <div className="absolute inset-0 bg-grid-pattern-emerald opacity-10" data-id="element-635"></div>

            <div className="flex flex-col lg:flex-row gap-16 items-center relative z-10" data-id="element-636">
              <div className="w-full lg:w-1/3" data-id="element-637">
                <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tighter leading-tight" data-id="element-638">
                  Resultados que puedes{' '}
                  <span className="text-gradient" data-id="element-639">medir</span>
                </h2>
                <p className="text-gray-400 mb-10 text-lg font-medium" data-id="element-640">
                  Nuestra metodología está diseñada para impactar directamente
                  en el crecimiento de tu negocio.
                </p>
                <Link to="/resultados" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-bold transition-all backdrop-blur-sm border border-white/10 hover:border-white/20 hover:-translate-y-1" data-id="element-641">
                  Ver todos los resultados
                </Link>
              </div>

              <div className="w-full lg:w-2/3 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-642">
                {metrics.map((metric, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="bg-[#0a0a0a]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center hover:border-brand-emerald/30 transition-colors group relative overflow-hidden" data-id="element-643">
                    <div className="text-brand-emerald flex justify-center mb-4 group-hover:scale-110 transition-transform duration-500 relative z-10" data-id="element-644">
                      {metric.icon}
                    </div>

                    {/* Mini Charts based on index */}
                    <div className="h-12 w-full flex items-center justify-center mb-2 relative z-10" data-id="element-645">
                      {index === 0 && <div className="flex items-end gap-1.5 h-8" data-id="element-646">
                          {[40, 60, 80, 100].map((h, i) => <motion.div key={i} initial={{
                      height: 0
                    }} whileInView={{
                      height: `${h}%`
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.8,
                      delay: 0.2 + i * 0.1
                    }} className="w-2 bg-brand-emerald rounded-sm" data-id="element-647" />)}
                        </div>}
                      {index === 1 && <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90" data-id="element-648">
                          <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" data-id="element-649" />
                          <motion.circle initial={{
                      strokeDasharray: '0 100'
                    }} whileInView={{
                      strokeDasharray: '85 100'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1.5,
                      delay: 0.3
                    }} cx="18" cy="18" r="14" fill="none" stroke="#10b981" strokeWidth="4" strokeLinecap="round" data-id="element-650" />
                        </svg>}
                      {index === 2 && <svg viewBox="0 0 50 20" className="w-16 h-8 overflow-visible" data-id="element-651">
                          <motion.path initial={{
                      pathLength: 0
                    }} whileInView={{
                      pathLength: 1
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      delay: 0.4
                    }} d="M0,20 Q10,15 20,18 T40,5 L50,0" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" data-id="element-652" />
                          <motion.circle initial={{
                      scale: 0
                    }} whileInView={{
                      scale: 1
                    }} viewport={{
                      once: true
                    }} transition={{
                      delay: 1.4
                    }} cx="50" cy="0" r="3" fill="#fff" data-id="element-653" />
                        </svg>}
                    </div>

                    <div className="text-5xl font-black text-white mb-3 tracking-tighter relative z-10 drop-shadow-[0_0_15px_rgba(16,185,129,0.2)] group-hover:drop-shadow-[0_0_20px_rgba(16,185,129,0.5)] transition-all duration-500" data-id="element-654">
                      {metric.value}
                    </div>
                    <div className="text-sm font-bold text-gray-400 uppercase tracking-wider relative z-10" data-id="element-655">
                      {metric.label}
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <PageCTA data-id="element-656" />
    </div>;
}